import React, { useRef, useState } from "react"
import { SortDir, FilterOption } from "../types"
import { Chevron } from "./Chevron"

let headerMenuSeq = 0

type Props = {
  label: React.ReactNode
  align?: "left" | "right"

  // --- Sorting ---
  sortable?: boolean
  active?: boolean // is this column the current sort column
  dir?: SortDir
  onSort?: (dir: SortDir) => void
  onClearSort?: () => void

  // --- Filtering ---
  filterable?: boolean
  // Enum-style filter: a fixed list of humanised options.
  filterOptions?: FilterOption[]
  activeFilter?: string | null
  onFilter?: (value: string | null) => void
  // Or a custom filter control (e.g. a type-ahead) rendered in the filter section.
  filterContent?: React.ReactNode
}

const DirArrow: React.FC<{ active: boolean; dir?: SortDir }> = ({ active, dir }) =>
  active ? <span className="fsp-admin__hmenu-dir">{dir === "asc" ? "↑" : "↓"}</span> : null

// A sortable/filterable table header.
// - Filterable columns show a chevron that opens a dropdown with a Sort section
//   and a Filter section separated by a horizontal rule (only the sections that
//   apply are shown).
// - Sort-only columns have no chevron/menu; clicking the header cycles the sort
//   (ascending → descending → cleared).
export const ColumnHeaderMenu: React.FC<Props> = ({
  label,
  align = "left",
  sortable = false,
  active = false,
  dir,
  onSort,
  onClearSort,
  filterable = false,
  filterOptions,
  activeFilter,
  onFilter,
  filterContent,
}) => {
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuId = useRef(`wb-hmenu-${headerMenuSeq++}`).current
  const showSort = sortable && !!onSort
  const showFilter = filterable && (!!filterOptions || !!filterContent)
  const filterActive = activeFilter != null && activeFilter !== ""
  const thClass = `fsp-admin__hmenu-th ${align === "right" ? "fsp-admin__hmenu-th--right" : ""}`

  // --- Sort-only: clicking the header toggles asc → desc → clear ---
  if (showSort && !showFilter) {
    const cycle = () => {
      if (!active) onSort!("asc")
      else if (dir === "asc") onSort!("desc")
      else onClearSort ? onClearSort() : onSort!("asc")
    }
    return (
      <th className={thClass}>
        <button
          type="button"
          className={`fsp-admin__hmenu-trigger ${active ? "fsp-admin__hmenu-trigger--active" : ""}`}
          onClick={cycle}
        >
          <span>{label}</span>
          <DirArrow active={active} dir={dir} />
        </button>
      </th>
    )
  }

  // --- Plain header (neither sortable nor filterable) ---
  if (!showFilter) {
    return (
      <th className={thClass}>
        <span>{label}</span>
      </th>
    )
  }

  // --- Filterable (optionally also sortable): chevron toggles the menu on click ---
  const close = (focusTrigger = false) => {
    setOpen(false)
    if (focusTrigger) triggerRef.current?.focus()
  }

  return (
    <th className={thClass}>
      <button
        ref={triggerRef}
        type="button"
        className={`fsp-admin__hmenu-trigger ${active || filterActive ? "fsp-admin__hmenu-trigger--active" : ""}`}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-controls={open ? menuId : undefined}
        aria-expanded={open}
      >
        <span>{label}</span>
        <DirArrow active={active} dir={dir} />
        <Chevron open={open} />
      </button>

      {open && <div className="fsp-admin__hmenu-backdrop" onClick={() => close()} />}

      {open && (
        <div
          id={menuId}
          className="fsp-admin__hmenu"
          role="menu"
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              e.preventDefault()
              close(true)
            }
          }}
        >
          {showSort && (
            <div className="fsp-admin__hmenu-section">
              <div className="fsp-admin__hmenu-heading">Sort</div>
              <button
                type="button"
                role="menuitem"
                className={`fsp-admin__hmenu-item ${active && dir === "asc" ? "fsp-admin__hmenu-item--active" : ""}`}
                onClick={() => {
                  onSort!("asc")
                  close()
                }}
              >
                Ascending
              </button>
              <button
                type="button"
                role="menuitem"
                className={`fsp-admin__hmenu-item ${active && dir === "desc" ? "fsp-admin__hmenu-item--active" : ""}`}
                onClick={() => {
                  onSort!("desc")
                  close()
                }}
              >
                Descending
              </button>
              {active && onClearSort && (
                <button
                  type="button"
                  role="menuitem"
                  className="fsp-admin__hmenu-item fsp-admin__hmenu-item--muted"
                  onClick={() => {
                    onClearSort()
                    close()
                  }}
                >
                  Clear sort
                </button>
              )}
            </div>
          )}

          {showSort && <hr className="fsp-admin__hmenu-rule" />}

          <div className="fsp-admin__hmenu-section">
            <div className="fsp-admin__hmenu-heading">Filter</div>
            {filterOptions ? (
              <>
                <button
                  type="button"
                  role="menuitem"
                  className={`fsp-admin__hmenu-item ${!filterActive ? "fsp-admin__hmenu-item--active" : ""}`}
                  onClick={() => {
                    onFilter?.(null)
                    close()
                  }}
                >
                  All
                </button>
                {filterOptions.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    role="menuitem"
                    className={`fsp-admin__hmenu-item ${activeFilter === opt.value ? "fsp-admin__hmenu-item--active" : ""}`}
                    onClick={() => {
                      onFilter?.(opt.value)
                      close()
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </>
            ) : (
              <div className="fsp-admin__hmenu-custom">{filterContent}</div>
            )}
          </div>
        </div>
      )}
    </th>
  )
}
